import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top instantly on route change
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, [pathname]);

  return null;
}
import { Login } from './views/Login';
import { WhatsAppConnection } from './views/WhatsAppConnection';
import { Dashboard } from './views/Dashboard';
import { AddClient } from './views/AddClient';
import { Chats } from './views/Chats';
import { Clients } from './views/Clients';
import { ClientDetails } from './views/ClientDetails';
import { ChatWorkspace } from './views/ChatWorkspace';
import { Templates } from './views/Templates';
import { TemplateCreate } from './views/TemplateCreate';
import { TemplateEdit } from './views/TemplateEdit';
import { TemplateBulkSend } from './views/TemplateBulkSend';
import { Analytics } from './views/Analytics';
import { Profile } from './views/Profile';
import { WhatsAppChatScreen } from './views/WhatsAppChatScreen';
import { AdminPanel } from './views/admin/AdminPanel';
import { UserManagement } from './views/admin/UserManagement';
import { ProfessionManagement } from './views/admin/ProfessionManagement';
import { ProfessionEdit } from './views/admin/ProfessionEdit';
import { SystemMonitor } from './views/admin/SystemMonitor';
import { Schedule } from './views/Schedule';
import { AppointmentDetail } from './views/AppointmentDetail';
import { NoteDetail } from './views/NoteDetail';
import { WhatsAppSyncRecoveryModal } from './components/WhatsAppSyncRecoveryModal';
import { useWhatsAppIncompleteSync } from './hooks/useWhatsAppStatus';
import { useWhatsAppSyncProgress } from './hooks/useWhatsAppSyncProgress';
import { Invoices } from './views/Invoices';
import { InvoiceDetail } from './views/InvoiceDetail';
import { Inventory } from './views/Inventory';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B8E87] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/" replace />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { profile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B8E87] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!profile || profile.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function RecoveryModalWrapper() {
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const incompleteSyncQuery = useWhatsAppIncompleteSync();
  const { startSync } = useWhatsAppSyncProgress();
  const location = useLocation();

  // Show recovery prompt when navigating to WhatsApp features
  useEffect(() => {
    if (
      incompleteSyncQuery.data?.hasIncompleteSync &&
      (location.pathname.includes('/chats') ||
       location.pathname.includes('/clients') ||
       location.pathname.includes('/whatsapp') ||
       location.pathname.includes('/chat/'))
    ) {
      setShowRecoveryModal(true);
    }
  }, [location.pathname, incompleteSyncQuery.data]);

  const handleResume = () => {
    startSync();
    setShowRecoveryModal(false);
  };

  return (
    <WhatsAppSyncRecoveryModal
      isOpen={showRecoveryModal}
      onClose={() => setShowRecoveryModal(false)}
      onResume={handleResume}
    />
  );
}

export function AppRouter() {
  return <BrowserRouter
    future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    }}
  >
      <ScrollToTop />
      <RecoveryModalWrapper />
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/connect" element={<WhatsAppConnection />} />
        <Route path="/dashboard" element={<ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute>
              <Profile />
            </ProtectedRoute>} />
        <Route path="/add-client" element={<ProtectedRoute>
              <AddClient />
            </ProtectedRoute>} />
        <Route path="/chats" element={<ProtectedRoute>
              <Chats />
            </ProtectedRoute>} />
        <Route path="/clients" element={<ProtectedRoute>
              <Clients />
            </ProtectedRoute>} />
        <Route path="/client/:clientId" element={<ProtectedRoute>
              <ClientDetails />
            </ProtectedRoute>} />
        <Route path="/client/:clientId/note/:noteId" element={<ProtectedRoute>
              <NoteDetail />
            </ProtectedRoute>} />
        <Route path="/chat/:clientId" element={<ProtectedRoute>
              <ChatWorkspace />
            </ProtectedRoute>} />
        <Route path="/templates" element={<ProtectedRoute>
              <Templates />
            </ProtectedRoute>} />
        <Route path="/templates/create" element={<ProtectedRoute>
              <TemplateCreate />
            </ProtectedRoute>} />
        <Route path="/templates/:id/edit" element={<ProtectedRoute>
              <TemplateEdit />
            </ProtectedRoute>} />
        <Route path="/templates/:id/bulk-send" element={<ProtectedRoute>
              <TemplateBulkSend />
            </ProtectedRoute>} />
        <Route path="/schedule" element={<ProtectedRoute>
              <Schedule />
            </ProtectedRoute>} />
        <Route path="/appointment/:appointmentId" element={<ProtectedRoute>
              <AppointmentDetail />
            </ProtectedRoute>} />
        <Route path="/invoices" element={<ProtectedRoute>
              <Invoices />
            </ProtectedRoute>} />
        <Route path="/invoices/:invoiceId" element={<ProtectedRoute>
              <InvoiceDetail />
            </ProtectedRoute>} />
        <Route path="/inventory" element={<ProtectedRoute>
              <Inventory />
            </ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute>
              <Analytics />
            </ProtectedRoute>} />
        <Route path="/whatsapp/chat/:jid" element={<ProtectedRoute>
              <WhatsAppChatScreen />
            </ProtectedRoute>} />
        <Route path="/admin" element={<AdminRoute>
              <AdminPanel />
            </AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute>
              <UserManagement />
            </AdminRoute>} />
        <Route path="/admin/professions" element={<AdminRoute>
              <ProfessionManagement />
            </AdminRoute>} />
        <Route path="/admin/professions/create" element={<AdminRoute>
              <ProfessionEdit />
            </AdminRoute>} />
        <Route path="/admin/professions/:professionId/edit" element={<AdminRoute>
              <ProfessionEdit />
            </AdminRoute>} />
        <Route path="/admin/monitor" element={<AdminRoute>
              <SystemMonitor />
            </AdminRoute>} />
      </Routes>
    </BrowserRouter>;
}
